import { useState, useEffect, useRef, useCallback } from 'react';
import { useConcurrentSearch } from '@/hooks/useConcurrentSearch';
import { getAiPromptType } from '@/util';
import { parseUnderwritingStatus } from '@/features/shop-insurance';

interface UnderwritingResult {
  status: 'eligible' | 'ineligible' | 'unsure' | null;
  fullData: OpenAiCompletionDbRecord | null;
}

interface UseUnderwritingSearchProps {
  clientId: string | undefined;
  productsToSearch: string[];
  isJoint: boolean;
  shouldStart: boolean;
}

type SearchKey = 'nw' | 'oa' | 'cs' | 'lincoln' | 'securian';

export function useUnderwritingSearch({
  clientId,
  productsToSearch,
  isJoint,
  shouldStart,
}: UseUnderwritingSearchProps) {
  const [fetchStarted, setFetchStarted] = useState(false);
  const [fetchComplete, setFetchComplete] = useState(false);
  const [results, setResults] = useState<Map<string, UnderwritingResult>>(new Map());

  const pendingSearches = useRef<Set<string>>(new Set());
  const searchResults = useRef<Map<string, UnderwritingResult>>(new Map());

  const nationwideSearch = useConcurrentSearch('ai_underwriting_guidance_nw');
  const assetCareSearch = useConcurrentSearch('ai_underwriting_guidance_oa');
  const careScoutSearch = useConcurrentSearch('ai_underwriting_guidance_cs');
  const securianSearch = useConcurrentSearch('ai_underwriting_guidance_securian');
  const lincolnSearch = useConcurrentSearch('ai_underwriting_guidance_lincoln');

  const updateResults = useCallback(() => {
    setResults(new Map(searchResults.current));
  }, []);

  useEffect(() => {
    if (!shouldStart || !clientId || productsToSearch.length === 0) {
      return;
    }

    pendingSearches.current.clear();
    searchResults.current.clear();
    setFetchComplete(false);
    setFetchStarted(true);

    productsToSearch.forEach(productName => {
      pendingSearches.current.add(productName);
    });

    const timeoutId = setTimeout(() => {
      if (pendingSearches.current.size > 0) {
        console.warn('Concurrent searches timed out, completing anyway');
        pendingSearches.current.clear();
        setFetchComplete(true);
      }
    }, 30000);

    const handleSearchComplete = (
      productName: string,
      aiPromptType: string,
      result: UnderwritingResult,
    ) => {
      pendingSearches.current.delete(productName);

      const resultKey = `${productName}:${aiPromptType}`;
      searchResults.current.set(resultKey, result);

      if (pendingSearches.current.size === 0) {
        clearTimeout(timeoutId);
        setFetchComplete(true);
        updateResults();
      }
    };

    const searchHookMap = {
      nw: nationwideSearch,
      oa: assetCareSearch,
      cs: careScoutSearch,
      securian: securianSearch,
      lincoln: lincolnSearch,
    };

    productsToSearch.forEach(productName => {
      const aiPromptType = getAiPromptType('', productName, isJoint);
      if (!aiPromptType) return;

      const carrierTypeMatch = aiPromptType.match(/-(nw|oa|cs|lincoln|securian)-/)?.[1];
      const searchKey =
        carrierTypeMatch && carrierTypeMatch in searchHookMap ? carrierTypeMatch : 'cs';
      const searchHook = searchHookMap[searchKey as SearchKey];
      if (searchHook) {
        searchHook.startSearch(
          {
            requesterId: clientId,
            aiPromptType,
          },
          {
            onSuccess: data => {
              const parsedStatus = parseUnderwritingStatus(data?.results);
              handleSearchComplete(productName, aiPromptType, {
                status: parsedStatus,
                fullData: data,
              });
            },
            onError: () => {
              handleSearchComplete(productName, aiPromptType, {
                status: null,
                fullData: null,
              });
            },
          },
        );
      }
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, [shouldStart, clientId, productsToSearch, isJoint]);

  return {
    fetchStarted,
    fetchComplete,
    results,
  };
}
